import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import EuroIcon from '@mui/icons-material/Euro'
import DownloadIcon from '@mui/icons-material/Download'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'

import Button from '../components/Button'
import Dialog from '../components/Dialog'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import ButtonDownload from '../components/ButtonDownload'
import SelectionBar from '../components/selection/SelectionBar'
import DialogRimborso from '../components/DialogRimborso'
import ButtonRunDownload from '../components/ButtonRunDownload'
import SortableTableHead from '../components/SortableTableHead'
import DialogRimborsoPagato from '../components/DialogRimborsoPagato'
import GridCircularProgress from '../components/GridCircularProgress'
import DialogAutocertificazione from '../components/DialogAutocertificazione'
import { config } from '../settings/configs'
import {
  colours,
  titlesRimborso as titles,
  phrasesRimborso as phrases,
} from '../settings/settings'
import { selectRimborsiSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import { useRunAlgorithm } from '../hooks/algoritmi-functions'
import {
  useGetRimborsiQuery,
  useCreateRimborsoMutation,
  useUpdateRimborsoMutation,
  useUpdateRimborsoIsPaidMutation,
  useDeleteRimborsoMutation,
} from '../hooks/api-slice'
import {
  fromDateTimeToString,
  sumFun,
  formatEuro,
  formatBodyField,
} from '../hooks/utils-functions'

const Rimborsi = () => {
  const { admin, arbitro, codiceUtente, idArbitro } = useSelector(
    state => state.home.infoUtente,
  )
  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const selectedArbitro = useSelector(state => state.selection.selectedArbitro)

  const [mode, setMode] = useState()
  const [rimborso, setRimborso] = useState()

  const urlStagione = `/stagione/${selectedStagione}`
  const urlSuffix = admin ? urlStagione : `/arbitro/${idArbitro}`
  const { isLoading } = useGetRimborsiQuery(urlSuffix, {
    skip: !((admin && selectedStagione) || (arbitro && idArbitro)),
  })
  const rimborsi = useSelector(state => selectRimborsiSorted(state, urlSuffix))

  const [createRimborsoMutation] = useCreateRimborsoMutation()
  const [updateRimborsoMutation] = useUpdateRimborsoMutation()
  const [updateRimborsoIsPaidMutation] = useUpdateRimborsoIsPaidMutation()
  const [deleteRimborsoMutation] = useDeleteRimborsoMutation()

  document.title = `${config?.name} - Rimborsi`

  let columnsRimborsi = [
    { label: 'Creazione', sortable: true, sortingField: 'timeFirstInsert' },
    { label: 'Stagione', sortable: false },
  ]
  if (admin)
    columnsRimborsi = [
      ...columnsRimborsi,
      { label: 'Arbitro', sortable: true, sortingField: 'cognomenome' },
    ]
  columnsRimborsi = [
    ...columnsRimborsi,
    { label: 'Gare', sortable: false },
    { label: 'Totale', sortable: true, sortingField: 'totale' },
    { label: 'Stato', sortable: true, sortingField: 'isPaid' },
    { label: 'Opzioni', sortable: false },
  ]

  const rimborsiN = rimborsi
    ?.filter(r =>
      admin && selectedArbitro ? r.idArbitro === selectedArbitro : true,
    )
    ?.filter(r => r.stagione === selectedStagione)

  const allPaid = rimborsi?.every(r => r.isPaid)
  const allPaidN = rimborsiN?.every(r => r.isPaid)

  const [openRimborso, setOpenRimborso] = useState()
  const [openPagato, setOpenPagato] = useState()
  const [openAutocert, setOpenAutocert] = useState()
  const [deletingRimborso, setDeletingRimborso] = useState()
  const [openDeleteRimborso, setOpenDeleteRimborso] = useState()

  const { setFeedback } = useFeedback()
  const { runAlgorithm } = useRunAlgorithm()

  const handleClickOpenRimborso = id => {
    setRimborso(rimborsiN?.find(r => r.id === id))
    setOpenRimborso(true)
  }

  const handleClickOpenPagato = id => {
    setRimborso(rimborsiN?.find(r => r.id === id))
    setOpenPagato(true)
  }

  const handleClickOpenAutocert = () => {
    setOpenAutocert(true)
  }

  const handleClickOpenDeleteRimborso = id => {
    setDeletingRimborso(rimborsiN?.find(r => r.id === id))
    setOpenDeleteRimborso(true)
  }

  const handleCloseRimborso = () => {
    setOpenRimborso()
    setMode()
    setRimborso()
  }

  const handleClosePagato = () => {
    setOpenPagato()
    setRimborso()
  }

  const handleCloseAutocert = () => {
    setOpenAutocert()
  }

  const handleCloseDeleteRimborso = () => {
    setOpenDeleteRimborso()
    setDeletingRimborso()
  }

  const confirmRimborso = async x => {
    if (mode === 'add') await addRimborso(x)
    if (mode === 'update') await updateRimborso(x)
    if (mode === 'view') handleCloseRimborso()
  }

  const confirmDeleteRimborso = async () => {
    await deleteRimborso()
    handleCloseDeleteRimborso()
  }

  const totTotaleDovuto = () =>
    formatEuro(
      sumFun(rimborsiN?.filter(r => !r.isPaid)?.map(r => r.totale)) || 0,
    )
  const totTotalePagato = () =>
    formatEuro(
      sumFun(rimborsiN?.filter(r => r.isPaid)?.map(r => r.totale)) || 0,
    )

  const addRimborso = async selectedDesignazioni => {
    if (!selectedDesignazioni?.length) {
      setFeedback('Nessuna gara selezionata!')
      return
    }

    const ids = selectedDesignazioni.map(d => ({ idDesignazione: d }))
    const body = { codiceArbitro: codiceUtente, designazioni: ids }
    const result = await createRimborsoMutation(body)
    if (!result.error) {
      handleCloseRimborso()
      setFeedback(phrases.phraseAddSuccess, false, titles.titleAddSuccess)
    }
  }

  const updateRimborso = async rimborso => {
    const arg = { id: rimborso?._id, designazioni: rimborso?.designazioni }
    const result = await updateRimborsoMutation(arg)
    if (!result.error) {
      handleCloseRimborso()
      setFeedback(phrases.phraseUpdateSuccess, false, titles.titleUpdateSuccess)
    }
  }

  const updatePagato = async (id, rimborsoDatePaid) => {
    const arg = {
      id,
      datePaid: formatBodyField(rimborsoDatePaid),
    }
    const result = await updateRimborsoIsPaidMutation(arg)
    if (!result.error) {
      handleClosePagato()
      setFeedback(phrases.phrasePagatoSuccess, false, titles.titlePagatoSuccess)
    }
  }

  const deleteRimborso = async () => {
    const result = await deleteRimborsoMutation(deletingRimborso?._id)
    if (!result.error)
      setFeedback(phrases.phraseDeleteSuccess, false, titles.titleDeleteSuccess)
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Rimborsi' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar yStagione yArbitro={admin} />
        {arbitro && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <Button
                onClick={() => {
                  setMode('add')
                  handleClickOpenRimborso()
                }}
              >
                Nuovo rimborso
              </Button>
            </center>
          </Grid>
        )}
        {arbitro && (
          <Grid size={{ xs: 12, md: 'grow' }} onClick={handleClickOpenAutocert}>
            <center>
              <Button excel>Autocertificazione</Button>
            </center>
          </Grid>
        )}
        {arbitro && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <ButtonDownload algorithm='21_InformativaArbitri' type='PDF' />
          </Grid>
        )}
        {admin && rimborsiN && (
          <>
            <Grid size={{ xs: 12, md: 7 }}>
              <ButtonRunDownload
                buttonText='Rimborsi da pagare'
                algorithm='18_GenerateExcelRimborsiBanca'
                type='XLSX'
                disabled={allPaid}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 'grow' }}>
              <center>
                Totale da rimborsare: <b>{totTotaleDovuto()}</b>
              </center>
            </Grid>
            <Grid size={{ xs: 12, md: 'grow' }}>
              <center>
                Totale rimborsato: <b>{totTotalePagato()}</b>
              </center>
            </Grid>
          </>
        )}
      </Grid>
      {(isLoading && <GridCircularProgress />) ||
        (!rimborsi && <GridHeader text='Elenco dei rimborsi non trovato.' />) ||
        (!rimborsiN?.length && (
          <GridHeader text='Nessun rimborso trovato.' />
        )) || (
          <Grid container sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={rimborsiN}
                  columns={columnsRimborsi}
                  tableName='rimborsi'
                />
                <TableBody>
                  {rimborsiN?.map(r => {
                    const kmToValidate = r?.designazioni?.filter(
                      d => typeof d.kmPercorsiToValidate !== 'undefined',
                    )?.length

                    return (
                      <TableRow
                        key={r.id}
                        sx={{
                          cursor: 'pointer',
                          ':hover': { backgroundColor: colours.greyVeryLight },
                        }}
                        onClick={() => {
                          setMode(r.isPaid ? 'view' : 'update')
                          handleClickOpenRimborso(r.id)
                        }}
                      >
                        <TableCell>
                          {fromDateTimeToString(
                            new Date(r?.timeFirstInsert),
                            true,
                          )}
                        </TableCell>
                        <TableCell>{r?.stagione}</TableCell>
                        {admin && <TableCell>{r?.cognomenome}</TableCell>}
                        <TableCell>
                          <Grid
                            container
                            alignItems='center'
                            sx={{ width: '100%' }}
                          >
                            <Grid size='grow'>
                              {r?.designazioni?.length}&nbsp;
                              {r?.designazioni?.length === 1 ? 'gara' : 'gare'}
                            </Grid>
                            {kmToValidate > 0 && (
                              <Grid size='auto'>
                                <Tooltip title='km da validare'>
                                  <PriorityHighIcon color='warning' />
                                </Tooltip>
                              </Grid>
                            )}
                          </Grid>
                        </TableCell>
                        <TableCell>{formatEuro(r?.totale)}</TableCell>
                        <TableCell>
                          <Chip
                            sx={{ cursor: 'pointer' }}
                            variant={r.isPaid ? 'outlined' : 'filled'}
                            label={r.isPaid ? 'pagato' : 'da pagare'}
                            color={r.isPaid ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          <Grid
                            container
                            sx={{ minWidth: '8vw', width: '100%' }}
                          >
                            <Grid size='grow'>
                              <IconButton
                                style={{ color: colours.greenExcel }}
                                onClick={e => {
                                  e.stopPropagation()
                                  runAlgorithm({
                                    algorithm: '13_GenerateExcelRimborso',
                                    selectedRimborso: r.id,
                                    exactlyOne: true,
                                    type: 'XLSX',
                                  })
                                }}
                              >
                                <Tooltip title='Scarica Excel'>
                                  <DownloadIcon />
                                </Tooltip>
                              </IconButton>
                            </Grid>
                            {admin && (
                              <Grid size='grow'>
                                <IconButton
                                  style={{
                                    color: r.isPaid
                                      ? colours.greenExcel
                                      : colours.greyVeryLight,
                                  }}
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleClickOpenPagato(r.id)
                                  }}
                                >
                                  <Tooltip title='Aggiorna pagamento'>
                                    <EuroIcon />
                                  </Tooltip>
                                </IconButton>
                              </Grid>
                            )}
                            {!allPaidN &&
                              ((r.isPaid && <Grid size='grow' />) || (
                                <GridIconButton
                                  type='delete'
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleClickOpenDeleteRimborso(r.id)
                                  }}
                                />
                              ))}
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <DialogRimborso
        open={openRimborso}
        handleClose={handleCloseRimborso}
        triggerFunction={confirmRimborso}
        rimborsoInit={rimborso}
        mode={mode}
      />
      <DialogRimborsoPagato
        open={openPagato}
        handleClose={handleClosePagato}
        triggerFunction={updatePagato}
        rimborso={rimborso}
      />
      <DialogAutocertificazione
        open={openAutocert}
        handleClose={handleCloseAutocert}
      />
      <Dialog
        open={openDeleteRimborso}
        handleClose={handleCloseDeleteRimborso}
        title={titles.titleDelete}
        textContent={phrases.phraseDelete}
        textUndo='Annulla'
        textConfirm='Ok'
        triggerFunction={confirmDeleteRimborso}
        error
      />
    </Grid>
  )
}

export default Rimborsi
